import { useTranslation } from 'react-i18next';

import { send } from '@actual-app/core/platform/client/connection';
import * as monthUtils from '@actual-app/core/shared/months';
import type { IntegerAmount } from '@actual-app/core/shared/util';
import type { ImportTransactionEntity } from '@actual-app/core/types/models';
import type { SyncedPrefs } from '@actual-app/core/types/prefs';
import { useQueryClient } from '@tanstack/react-query';

import { accountQueries } from '#accounts/queries';
import { categoryQueries } from '#budget/queries';
import { escribirTipo } from '#components/mobile/accounts/cuentas';
import { parsePagosFijos } from '#components/mobile/budget/pagadas';
import {
  asegurarCategoriasHistoria,
  categoriasGasto,
  crearCuentaConHistoria,
  cuadrar,
  cuadrarMesActual,
  desordenarHistoria,
  gruposPorComercio,
  marcarHistoria,
  marcarMesActual,
  mediasHistoria,
  ordenarHistoria,
} from '#components/mobile/historia/escribirHistoria';
import type { GrupoHistoria } from '#components/mobile/historia/escribirHistoria';
import {
  leerHistoria,
  PREF_HISTORIA,
} from '#components/mobile/historia/historia';
import { saveSyncedPrefs } from '#prefs/prefsSlice';
import { useDispatch, useSelector, useStore } from '#redux';

import { MENSUALES, OCASIONALES } from './catalogo';
import type { IdCofre, MetaElegida } from './catalogo';
import * as escribir from './escribir';
import type { CuentaTravesia, OpcionesCrear } from './escribir';
import { actualizarTravesia, leerTravesia, ULTIMO_PASO } from './estado';
import type { EstadoTravesia, IdsTravesia } from './estado';
import type { DatosRumbo } from './pasoRumbo';
import { cambiosReparto, cofresDeTravesia, repartoAuto } from './reparto';
import type { Asignado } from './reparto';

/**
 * Escritura de la «Primera travesía» por actos, con `send` real. Tras cada
 * acto guarda en la pref `travesia` lo creado (para retomar sin duplicar) y
 * refresca las listas de categorías y cuentas, para que el Plan y Cuentas
 * de detrás se llenen.
 */
export function useEscribirTravesia() {
  const { t } = useTranslation();
  const store = useStore();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const raw = useSelector(state => state.prefs.synced.travesia);
  const estado = leerTravesia(raw);

  /** Prefs sincronizadas en este momento (no las del último render). */
  function prefs() {
    return store.getState().prefs.synced;
  }

  function idsActuales(): IdsTravesia | undefined {
    return leerTravesia(prefs().travesia)?.ids;
  }

  function opciones(): OpcionesCrear {
    return {
      mes: monthUtils.currentMonth(),
      hoy: monthUtils.currentDay(),
      coloresRaw: prefs()['category-colors'],
      ids: idsActuales(),
    };
  }

  async function guardar(cambios: SyncedPrefs) {
    await dispatch(saveSyncedPrefs({ prefs: cambios }));
  }

  async function refrescar() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: categoryQueries.lists() }),
      queryClient.invalidateQueries({ queryKey: accountQueries.lists() }),
    ]);
  }

  /** Mezcla cambios en la pref `travesia` (paso, perfil, estado…). */
  async function guardarEstado(
    cambios: Partial<Omit<EstadoTravesia, 'ids'>> & { ids?: IdsTravesia },
  ) {
    await guardar({ travesia: actualizarTravesia(prefs().travesia, cambios) });
  }

  /** Paso 2: borra las categorías de fábrica (una sola vez). */
  async function limpiarFabrica() {
    if (idsActuales()?.limpiado) {
      return;
    }
    await escribir.limpiarFabrica(send, t);
    await guardarEstado({ ids: { limpiado: true } });
    await refrescar();
  }

  /** Paso 5: crea las cuentas (Corriente, en el presupuesto). */
  async function crearCuentas(cuentas: readonly CuentaTravesia[]) {
    const { ids, tiposRaw } = await escribir.crearCuentas(
      send,
      cuentas,
      prefs()['cuentas-tipos'],
    );
    await guardar({
      'cuentas-tipos': tiposRaw,
      travesia: actualizarTravesia(prefs().travesia, { ids: { cuentas: ids } }),
    });
    await refrescar();
    return ids;
  }

  /**
   * Paso 5 con extracto: la cuenta del banco nace con su historia (saldo
   * inicial el día antes del primer movimiento) y, si hay, la de efectivo.
   * Lo de antes de este mes queda como historia: revisado, sin categoría
   * en la oculta y cada mes cuadrado, para que lo por repartir sea el
   * saldo.
   */
  async function crearCuentasConExtracto(
    banco: {
      nombre: string;
      saldoHoy: IntegerAmount;
      movimientos: readonly ImportTransactionEntity[];
    },
    efectivo: CuentaTravesia | null,
  ) {
    const ids: string[] = [];
    let tiposRaw = prefs()['cuentas-tipos'] ?? '';
    const idBanco = await crearCuentaConHistoria(
      send,
      banco.nombre,
      banco.saldoHoy,
      banco.movimientos,
    );
    ids.push(idBanco);
    tiposRaw = escribirTipo(tiposRaw, idBanco, 'corriente');
    if (efectivo) {
      const res = await escribir.crearCuentas(send, [efectivo], tiposRaw);
      ids.push(...res.ids);
      tiposRaw = res.tiposRaw;
    }
    const mes = monthUtils.currentMonth();
    const previa = leerHistoria(prefs()[PREF_HISTORIA]);
    const historia = {
      inicio: previa?.inicio ?? mes,
      ...(await asegurarCategoriasHistoria(send, t, previa)),
    };
    await guardar({
      'cuentas-tipos': tiposRaw,
      [PREF_HISTORIA]: JSON.stringify(historia),
      travesia: actualizarTravesia(prefs().travesia, { ids: { cuentas: ids } }),
    });
    await marcarHistoria(send, historia);
    await marcarMesActual(send, historia, historia.inicio);
    const { list } = (await send('get-categories')) as {
      list: Parameters<typeof categoriasGasto>[0];
    };
    const gasto = categoriasGasto(list);
    await cuadrar(send, historia.inicio, gasto);
    await cuadrarMesActual(send, historia.inicio, gasto);
    await refrescar();
    return ids;
  }

  /** Tras el extracto: los comercios que Gavi sabe ordenar. */
  async function gruposHistoria(): Promise<GrupoHistoria[]> {
    const historia = leerHistoria(prefs()[PREF_HISTORIA]);
    if (!historia) {
      return [];
    }
    return gruposPorComercio(
      send,
      t,
      historia,
      idsActuales()?.categorias ?? {},
    );
  }

  /** «Sí, ordénalos» (o «Deshacer» con `deshacer`). */
  async function ordenar(grupos: readonly GrupoHistoria[], deshacer = false) {
    const historia = leerHistoria(prefs()[PREF_HISTORIA]);
    if (!historia) {
      return 0;
    }
    let n = 0;
    if (deshacer) {
      await desordenarHistoria(send, historia, grupos);
    } else {
      n = await ordenarHistoria(send, historia, grupos);
    }
    await refrescar();
    return n;
  }

  /** Pasos 3 y 4: crea los cofres elegidos con su color y objetivo. */
  async function crearCategorias(elegidos: readonly IdCofre[]) {
    const res = await escribir.crearCategorias(send, t, elegidos, opciones());
    await guardar({
      'category-colors': res.coloresRaw,
      travesia: actualizarTravesia(prefs().travesia, {
        ids: { grupos: res.grupos, categorias: res.categorias },
      }),
    });
    await refrescar();
    return res.categorias;
  }

  /** Paso 6: crea la meta en el grupo Objetivos. */
  async function crearMeta(meta: MetaElegida) {
    const res = await escribir.crearMeta(send, t, meta, opciones());
    await guardar({
      'category-colors': res.coloresRaw,
      travesia: actualizarTravesia(prefs().travesia, {
        ids: {
          grupos: { obj: res.grupo },
          meta: { id: res.categoria, clave: meta.id, cuota: res.cuota },
        },
      }),
    });
    await refrescar();
    return res.categoria;
  }

  /**
   * Paso 6: crea (o reutiliza) la categoría Rumbo, la marca como pago fijo
   * y guarda su id en la travesía.
   */
  async function crearRumbo(nombre: string, datos: DatosRumbo) {
    const res = await escribir.crearRumbo(send, t, nombre, datos, {
      ...opciones(),
      idPrevio: idsActuales()?.rumbo?.id,
    });
    const fijos = new Set(parsePagosFijos(prefs()['pagos-fijos']));
    fijos.add(res.id);
    await guardar({
      'category-colors': res.coloresRaw,
      'pagos-fijos': JSON.stringify([...fijos]),
      travesia: actualizarTravesia(prefs().travesia, {
        ids: { rumbo: { id: res.id } },
      }),
    });
    await refrescar();
    return res.id;
  }

  /**
   * Paso 6: aparta el precio en el mes del fin de la prueba; o, con
   * «Aún no tengo dinero», lo deja pendiente para que Gavi lo retome.
   */
  async function apartarRumbo(datos: DatosRumbo, pendiente: boolean) {
    const id = idsActuales()?.rumbo?.id;
    if (!id) {
      return;
    }
    if (!pendiente) {
      await escribir.apartarRumbo(send, id, datos);
    }
    await guardarEstado({
      ids: { rumbo: pendiente ? { id, pendiente: true } : { id } },
    });
  }

  /**
   * Paso 8, «Auto»: reparte lo que queda (lo sugerido a cada cofre; lo que
   * sobra, a la meta) y lo escribe en el mes en curso.
   *
   * @param toBudget «Listos para asignar» ahora (céntimos).
   * @param asignado Lo asignado ahora a cada cofre (id real → céntimos).
   * @returns Lo asignado tras el reparto.
   */
  async function repartirAuto(toBudget: IntegerAmount, asignado: Asignado) {
    // Con historia, lo sugerido es lo que se suele gastar (lo ya gastado
    // este mes está asignado, así que se sugiere lo que falta).
    const historia = leerHistoria(prefs()[PREF_HISTORIA]);
    let medias: Map<string, IntegerAmount> | undefined;
    if (historia) {
      const { list } = await send('get-categories');
      medias = await mediasHistoria(historia.inicio, categoriasGasto(list));
    }
    const cofres = cofresDeTravesia(idsActuales(), medias);
    const yaAsignado = cofres.reduce((a, c) => a + (asignado[c.id] ?? 0), 0);
    const despues = repartoAuto(cofres, asignado, toBudget + yaAsignado);
    await escribir.escribirReparto(
      send,
      monthUtils.currentMonth(),
      cambiosReparto(asignado, despues),
    );
    return despues;
  }

  /** Paso 9: fija en Inicio la meta y las dos primeras categorías. */
  async function fijarInicio() {
    const ids = idsActuales();
    if (!ids) {
      return;
    }
    const orden = [...MENSUALES, ...OCASIONALES].map(c => c.id);
    await guardar({
      'pinned-categories': escribir.fijarInicio(
        prefs()['pinned-categories'],
        ids,
        orden,
      ),
    });
  }

  /** «Empezar a navegar»: la travesía queda hecha. */
  async function terminar() {
    await guardarEstado({ estado: 'hecha', paso: ULTIMO_PASO });
  }

  /**
   * «Saltar»: 7 cofres básicos (6 + fondo de emergencia), sin cuentas, y la
   * travesía queda `saltada`. También para aplicarla en silencio en
   * pantalla ancha.
   */
  async function saltar() {
    const res = await escribir.saltar(send, t, opciones());
    await guardar({
      'category-colors': res.coloresRaw,
      travesia: actualizarTravesia(prefs().travesia, {
        estado: 'saltada',
        ids: res.ids,
      }),
    });
    await refrescar();
  }

  return {
    estado,
    guardarEstado,
    limpiarFabrica,
    crearCuentas,
    crearCuentasConExtracto,
    gruposHistoria,
    ordenar,
    crearCategorias,
    crearMeta,
    crearRumbo,
    apartarRumbo,
    repartirAuto,
    fijarInicio,
    terminar,
    saltar,
  };
}

export type EscribirTravesia = ReturnType<typeof useEscribirTravesia>;
